/**
 * CreateFolderModal — Modal for creating a new folder.
 *
 * Opens when user clicks "Create Folder" in the action bar.
 * Creates the folder at the current level:
 *   - Root level: parent_folder_id = null
 *   - Inside a folder: parent_folder_id = currentFolderId
 *
 * Owns the create mutation, local name state, and toast copy. Delegates
 * the entire UI to the shared presentational `FolderNameModal`.
 */

import { useState, useCallback } from "react";
import FolderNameModal from "../FolderNameModal";
import { useCreateFolderMutation } from "../../../../../../../../../../redux/services/kbFilesApi";
import { customToast } from "../../../../../../../../../../utils/custom-toast/custom-toast";
import type { CreateFolderModalProps } from "../../../types/fileManagement.types";

const CreateFolderModal = ({
  open,
  onOpenChange,
  parentFolderId,
}: CreateFolderModalProps) => {
  const [folderName, setFolderName] = useState("");
  const [createFolder, { isLoading }] = useCreateFolderMutation();

  const trimmedName = folderName.trim();
  const isSubmitDisabled = isLoading || trimmedName.length === 0;

  const handleClose = useCallback(() => {
    setFolderName("");
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async () => {
    if (!trimmedName) return;

    try {
      await createFolder({
        name: trimmedName,
        parent_folder_id: parentFolderId,
      }).unwrap();

      customToast("Folder created successfully", "success");
      handleClose();
    } catch (error) {
      const errorDetail =
        (error as { data?: { detail?: string } })?.data?.detail ||
        "Failed to create folder";
      customToast(errorDetail, "error");
    }
  }, [trimmedName, parentFolderId, createFolder, handleClose]);

  return (
    <FolderNameModal
      open={open}
      title="New Folder"
      value={folderName}
      onChange={setFolderName}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isSubmitting={isLoading}
      isSubmitDisabled={isSubmitDisabled}
      submitLabel={isLoading ? "Creating..." : "Create Folder"}
    />
  );
};

export default CreateFolderModal;
